// components/create/InvitationMusicPicker.js
//
// The invitation's song: search YouTube (through /api/youtube/search, which
// caches and keeps the key server-side), click a video, done. It plays in a
// card at the end of the invitation (components/InvitationMusic.js).
//
// Searches when typing pauses: each search spends 100 of the day's quota.

import React, { useEffect, useRef, useState } from 'react';
import { FiSearch, FiCheck } from 'react-icons/fi';
import { parseMusicSearch, toInvitationMusic } from '../../shared/invitationMusic';

const SEARCH_DELAY_MS = 600;

export default function InvitationMusicPicker({ value, onChange, eventType, onPicked }) {
  const [query, setQuery] = useState(() => (eventType ? `${eventType} song` : ''));
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const latest = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return undefined; }
    setLoading(true);
    const timer = setTimeout(async () => {
      const id = ++latest.current;
      try {
        const res = await fetch(`/api/youtube/search?q=${encodeURIComponent(q)}`);
        const data = await res.json().catch(() => ({}));
        if (id !== latest.current) return;
        if (!res.ok) throw new Error(data.error || 'Search failed');
        setResults(parseMusicSearch(data));
        setError(null);
      } catch (err) {
        if (id === latest.current) setError(err.message || 'Could not search right now.');
      } finally {
        if (id === latest.current) setLoading(false);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="music-picker">
      <p className="music-picker-hint">
        A song from YouTube, played at the end of your invitation when guests reach it.
      </p>

      {value && (
        <div className="music-picker-current">
          {value.thumbnail && <img src={value.thumbnail} alt="" />}
          <div className="music-picker-text">
            <strong>{value.title}</strong>
            {value.channel && <span>{value.channel}</span>}
          </div>
          <button type="button" className="music-picker-remove" onClick={() => onChange(null)}>Remove</button>
        </div>
      )}

      <label className="music-picker-search">
        <FiSearch aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search YouTube for a song or video"
          aria-label="Search YouTube"
        />
        {loading && <span className="music-picker-spinner" aria-label="Searching" />}
      </label>

      {error && <p className="music-picker-error">{error}</p>}

      <ul className="music-picker-results">
        {results.map((row) => {
          const selected = value?.videoId === row.videoId;
          return (
            <li key={row.videoId}>
              <button
                type="button"
                className={`music-picker-row${selected ? ' is-selected' : ''}`}
                onClick={() => { onChange(toInvitationMusic(row)); onPicked?.(); }}
              >
                {row.thumbnail ? <img src={row.thumbnail} alt="" loading="lazy" /> : <span className="music-picker-thumb" />}
                <span className="music-picker-text">
                  <strong>{row.title}</strong>
                  {row.channel && <span>{row.channel}</span>}
                </span>
                {selected && <FiCheck aria-hidden="true" />}
              </button>
            </li>
          );
        })}
        {!loading && !error && query.trim().length >= 2 && results.length === 0 && (
          <li className="music-picker-empty">No videos found. Try other words.</li>
        )}
      </ul>
    </div>
  );
}
